import React, { useMemo, useState, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { buildPath, sortMenus } from "../../utils/menuRouting";
import BrandLogo from "../common/BrandLogo";

const routeAliasGroups = [
  ["/country", "/master/country", "/settings-master/country", "/countries"],
  ["/vendor", "/master/vendor", "/settings-master/vendor", "/vendors"],
  ["/stock-category", "/master/stock-category", "/settings-master/stock-category", "/stock-categories"],
  ["/currency-uom", "/settings-master/currency-uom"],
  ["/lot", "/settings-master/lot"],
  ["/yard", "/settings-master/yard"],
  ["/expense-category", "/settings-master/expense-category", "/expense-categories", "/settings-master/expense-categories"],
  ["/expense-overhead", "/settings-master/expense-overhead"],
  ["/selling-master", "/invoice-management/selling-master"],
  ["/bank", "/bank-master", "/master/bank", "/settings-master/bank", "/settings-master/bank-master"],
  ["/mis-reports", "/msi-reports"],
];

function getModuleRoot(path) {
  if (!path) return path;
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 0) return path;

  const lastPart = parts[parts.length - 1];
  if (["new", "create"].includes(lastPart)) {
    return `/${parts.slice(0, -1).join("/")}`;
  }

  if (["edit", "update"].includes(lastPart)) {
    return `/${parts.slice(0, -1).join("/")}`;
  }

  const editIndex = parts.findIndex((part) => ["edit", "update"].includes(part));
  if (editIndex > 0) {
    return `/${parts.slice(0, editIndex).join("/")}`;
  }

  if (parts.length > 1 && /^\d+$/.test(parts[parts.length - 1])) {
    return `/${parts.slice(0, -1).join("/")}`;
  }

  return path;
}

function getRouteAliases(navPath) {
  const moduleRoot = getModuleRoot(navPath);
  const aliasGroup = routeAliasGroups.find((group) => group.includes(moduleRoot));
  return aliasGroup || [moduleRoot];
}

function isRouteActive(currentPath, navPath) {
  if (!navPath) return false;
  const activePath = getModuleRoot(currentPath);
  return getRouteAliases(navPath).some(
    (path) => activePath === path || activePath.startsWith(`${path}/`)
  );
}

function MenuNode({
  menu,
  depth,
  isCollapsed,
  onMobileClose,
  openCollapsedMenuId,
  setOpenCollapsedMenuId,
  isOpen,
  onToggle,
}) {
  const [openChildId, setOpenChildId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const path = buildPath(menu?.controllerName, menu?.actionName);
  const navPath = path?.includes("/:") ? path.split("/:")[0] : path;
  const menuKey = menu?.id || `${menu?.controllerName}-${menu?.actionName}`;
  const children = useMemo(() => (Array.isArray(menu?.children) ? menu.children : []), [menu?.children]);
  const hasChildren = children.length > 0;
  const canRead = Boolean(menu?.permissions?.isRead);

  const isChildActive = useMemo(() => {
    const checkActive = (nodes) => {
      return nodes.some((node) => {
        const p = buildPath(node?.controllerName, node?.actionName);
        const np = p?.includes("/:") ? p.split("/:")[0] : p;
        if (isRouteActive(location.pathname, np)) return true;
        if (Array.isArray(node.children) && node.children.length > 0) {
          return checkActive(node.children);
        }
        return false;
      });
    };
    return checkActive(children);
  }, [children, location.pathname]);

  const isCurrentActive = isRouteActive(location.pathname, navPath);
  const isCollapsedParentOpen = isCollapsed && hasChildren && openCollapsedMenuId === menuKey;
  const isCollapsedParentActive = isCollapsed && hasChildren && isChildActive;
  const firstChildNavPath = useMemo(() => {
    const queue = [...children];
    while (queue.length > 0) {
      const node = queue.shift();
      if (!node?.permissions?.isRead) continue;
      const childPath = buildPath(node?.controllerName, node?.actionName);
      const childNavPath = childPath?.includes("/:") ? childPath.split("/:")[0] : childPath;
      if (childNavPath) return childNavPath;
      if (Array.isArray(node?.children) && node.children.length > 0) {
        queue.push(...node.children);
      }
    }
    return null;
  }, [children]);

  // The menu is open if it's manually toggled (browsing) OR if it's the active parent of the current route
  const isThisOpen = isOpen || (!isCollapsed && (isChildActive || isCurrentActive));

  if (!canRead) return null;

  const content = (
    <>
      <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg transition-colors duration-200 ${isCurrentActive || isCollapsedParentActive
          ? "bg-primary-500 text-white"
          : "bg-slate-100 text-slate-500 group-hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:group-hover:bg-slate-700"
        }`}>
        {menu?.iconClass ? <i className={`${menu.iconClass} text-[11px]`} /> : <span className="text-[10px] font-bold">{menu?.menuName?.charAt(0)}</span>}
      </span>
      {!isCollapsed && (
        <>
          <span
            className="ml-2.5 truncate font-semibold transition-opacity duration-300 flex-1 text-left"
            title={menu?.menuName}
          >
            {menu?.menuName}
          </span>
          {hasChildren && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              className={`h-3.5 w-3.5 text-slate-400 transition-transform duration-300 ${isThisOpen ? "rotate-180" : ""
                }`}
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </>
      )}
    </>
  );

  const baseClasses = `group flex items-center rounded-lg py-1 ${depth === 0 ? "text-sm" : "text-xs"
    } transition-all duration-200 w-full ${isCollapsed ? "justify-center px-1" : "px-2"
    }`;
  const activeClasses = "bg-primary-50 text-primary-600 dark:bg-primary-900/10 dark:text-primary-400";
  const inactiveClasses = "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/50 hover:text-primary-600";
  const collapsedParentActiveClasses = "bg-primary-50 text-primary-600 dark:bg-primary-900/10 dark:text-primary-400";
  const collapsedParentInactiveClasses = "text-slate-600 dark:text-slate-400";

  return (
    <div
      className="relative space-y-0.5"
      onMouseLeave={() => {
        if (isCollapsed && hasChildren && openCollapsedMenuId === menuKey) {
          setOpenCollapsedMenuId(null);
        }
      }}
    >
      {navPath && !hasChildren ? (
        <NavLink
          to={navPath}
          onClick={() => {
            setOpenCollapsedMenuId(null);
            onMobileClose();
          }}
          className={() =>
            `${baseClasses} ${isCurrentActive ? activeClasses : inactiveClasses}`
          }
          style={{ marginLeft: depth > 0 && !isCollapsed ? `${depth * 8}px` : undefined }}
        >
          {content}
        </NavLink>
      ) : (
        <button
          type="button"
          onClick={() => {
            if (isCollapsed && hasChildren) {
              if (firstChildNavPath) {
                setOpenCollapsedMenuId(null);
                navigate(firstChildNavPath);
                onMobileClose();
                return;
              }
              setOpenCollapsedMenuId((prev) => (prev === menuKey ? null : menuKey));
              return;
            }
            onToggle();
          }}
          className={`${baseClasses} ${isThisOpen && !isCollapsed ? "bg-slate-50/50 dark:bg-slate-800/20" : ""
            } ${isCollapsed && hasChildren
              ? isCollapsedParentActive
                ? collapsedParentActiveClasses
                : collapsedParentInactiveClasses
              : inactiveClasses
            }`}
          style={{ marginLeft: depth > 0 && !isCollapsed ? `${depth * 8}px` : undefined }}
        >
          {content}
        </button>
      )}

      {hasChildren && !isCollapsed && (
        <div
          className={`grid transition-all duration-300 ease-in-out ${isThisOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
            }`}
        >
          <div className="overflow-hidden">
            <div className="mt-0.5 space-y-0.5">
              {children.map((child) => {
                const childKey = child?.id || `${child?.controllerName}-${child?.actionName}`;
                return (
                  <MenuNode
                    key={childKey}
                    menu={child}
                    depth={depth + 1}
                    isCollapsed={isCollapsed}
                    onMobileClose={onMobileClose}
                    openCollapsedMenuId={openCollapsedMenuId}
                    setOpenCollapsedMenuId={setOpenCollapsedMenuId}
                    isOpen={openChildId === childKey}
                    onToggle={(force) => setOpenChildId(prev => (force || prev !== childKey ? childKey : null))}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

      {hasChildren && isCollapsed && isCollapsedParentOpen && (
        <div
          className="absolute left-full top-0 z-50 ml-2 min-w-52 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl dark:border-slate-700 dark:bg-slate-900"
          onMouseEnter={() => setOpenCollapsedMenuId(menuKey)}
          onMouseLeave={() => setOpenCollapsedMenuId(null)}
        >
          <div className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {menu?.menuName}
          </div>
          <div className="mt-1 space-y-0.5">
            {children.map((child) => {
              const childPath = buildPath(child?.controllerName, child?.actionName);
              const childNavPath = childPath?.includes("/:") ? childPath.split("/:")[0] : childPath;
              const isChildRouteActive = isRouteActive(location.pathname, childNavPath);
              const childCanRead = Boolean(child?.permissions?.isRead);

              if (!childCanRead || !childNavPath) return null;

              return (
                <NavLink
                  key={child?.id || `${child?.controllerName}-${child?.actionName}`}
                  to={childNavPath}
                  onClick={() => {
                    setOpenCollapsedMenuId(null);
                    onMobileClose();
                  }}
                  className={`flex items-center rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${isChildRouteActive
                      ? "bg-primary-50 text-primary-600 dark:bg-primary-900/10 dark:text-primary-400"
                      : "text-slate-600 hover:bg-slate-50 hover:text-primary-600 dark:text-slate-300 dark:hover:bg-slate-800"
                    }`}
                >
                  {child?.menuName}
                </NavLink>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const Sidebar = ({
  isMobileOpen = false,
  onMobileClose = () => { },
  isCollapsed: isCollapsedProp = false,
}) => {
  const { myMenus } = useAuth();
  const sortedMenus = useMemo(() => sortMenus(myMenus), [myMenus]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openCollapsedMenuId, setOpenCollapsedMenuId] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  // Use a local effective isCollapsed state that expands on hover
  const isCollapsed = isCollapsedProp && !isHovered && !isMobileOpen;

  useEffect(() => {
    if (!isCollapsed) {
      setOpenCollapsedMenuId(null);
    }
  }, [isCollapsed]);

  return (
    <>
      <div
        className={`fixed inset-0 z-30 bg-slate-900/20 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${isMobileOpen ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        onClick={onMobileClose}
      />
      <aside
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setOpenCollapsedMenuId(null);
        }}
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-200 bg-white transition-all duration-300 dark:border-slate-800 dark:bg-slate-900 lg:static lg:relative lg:z-30 lg:translate-x-0 ${isMobileOpen ? "translate-x-0" : "-translate-x-full"
          } ${isCollapsed ? "lg:w-20" : "lg:w-64"}`}
      >
        <div className={`flex border-b border-slate-200/80 px-3.5 dark:border-slate-800/80 ${isCollapsed ? "h-14 flex-col items-center justify-center gap-1" : "h-14 items-center justify-between"
          }`}>
          {!isCollapsed ? (
            <Link to="/dashboard" onClick={onMobileClose} className="flex items-center">
              <BrandLogo size="md" className="min-w-0" />
            </Link>
          ) : (
            <Link to="/dashboard" onClick={onMobileClose} className="flex items-center justify-center">
              <BrandLogo size="sm" compact className="opacity-95" />
            </Link>
          )}
          <div className={`flex items-center ${isCollapsed ? "" : "gap-1"}`}>
            {isMobileOpen && (
              <button
                type="button"
                onClick={onMobileClose}
                className="rounded-xl p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-1 no-scrollbar">
          {sortedMenus.map((menu) => {
            const menuKey = menu?.id || `${menu?.controllerName}-${menu?.actionName}`;
            return (
              <MenuNode
                key={menuKey}
                menu={menu}
                depth={0}
                isCollapsed={isCollapsed}
                onMobileClose={onMobileClose}
                openCollapsedMenuId={openCollapsedMenuId}
                setOpenCollapsedMenuId={setOpenCollapsedMenuId}
                isOpen={openMenuId === menuKey}
                onToggle={(force) => setOpenMenuId(prev => (force || prev !== menuKey ? menuKey : null))}
              />
            );
          })}

          {/* <div className="pt-4 mt-4 border-t border-slate-100 dark:border-slate-800">
            <NavLink
              to="/purchase-master-new-design"
              onClick={onMobileClose}
              className={({ isActive }) => 
                `group flex items-center rounded-xl py-1.5 text-xs transition-all duration-200 w-full ${
                  isCollapsed ? "justify-center px-1.5" : "px-2.5"
                } ${
                  isActive 
                    ? "bg-primary-50 text-primary-600 dark:bg-primary-900/10 dark:text-primary-400" 
                    : "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800/50 hover:text-primary-600"
                }`
              }
            >
              <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg transition-colors duration-200 ${
                isRouteActive(location.pathname, "/purchase-master-new-design")
                  ? "bg-primary-500 text-white" 
                  : "bg-slate-100 text-slate-500 group-hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:group-hover:bg-slate-700"
              }`}>
                <i className="fas fa-plus text-[10px]" />
              </span>
              {!isCollapsed && (
                <span className="ml-2.5 truncate font-bold transition-opacity duration-300 flex-1 text-left uppercase tracking-tight">
                  Add Purchase (New Design)
                </span>
              )}
            </NavLink>
          </div> */}
        </nav>

      </aside>
    </>
  );
};

export default Sidebar;
